import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./NotificationManagementPage.css";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "N/A";
  }
};

const TABS = [
  { key: "sendToUser", label: "Send to User", icon: "fa-user" },
  { key: "bulkNotification", label: "Bulk Notification", icon: "fa-users" },
  { key: "announceToAll", label: "Announce To All", icon: "fa-broadcast-tower" },
  { key: "managementNotification", label: "Notification List", icon: "fa-list" },
];

const NotificationManagementPage = () => {
  const [activeTab, setActiveTab] = useState("sendToUser");
  const [notifications, setNotifications] = useState([]);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationLink, setNotificationLink] = useState("");
  const [userInput, setUserInput] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const userSearchRequestRef = useRef(0);

  const counts = useMemo(() => ({
    total: notifications.length,
    active: notifications.filter((n) => n.isActive === true).length,
    inactive: notifications.filter((n) => n.isActive === false).length,
  }), [notifications]);

  const resetInput = () => {
    setNotificationTitle("");
    setNotificationMessage("");
    setNotificationLink("");
    setUserInput("");
    setSelectedUsers([]);
    setSuggestions([]);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSubmitMessage({ type: "", text: "" });
    resetInput();
  };

  const fetchNotificationList = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const res = await AdminService.getAllNotificationList();
      if (res?.success) {
        setNotifications(Array.isArray(res?.data) ? res.data : []);
      } else {
        setListError(res?.message || "Failed to fetch notification list.");
      }
    } catch (err) {
      setListError(err?.response?.data?.message || err?.message || "Failed to fetch notification list.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "managementNotification") {
      fetchNotificationList();
    }
  }, [activeTab, fetchNotificationList]);

  const handleFindUser = (input) => {
    setUserInput(input);
    setUserSearchError("");
    setSubmitMessage({ type: "", text: "" });
    if (!input || !input.trim()) {
      setSuggestions([]);
      setUserSearchLoading(false);
      return;
    }
    const query = input.trim();
    const requestId = Date.now();
    userSearchRequestRef.current = requestId;
    setUserSearchLoading(true);
    AdminService.findUserByEmailForNotification(query)
      .then((res) => {
        if (userSearchRequestRef.current !== requestId) return;
        if (res?.success) {
          const list = Array.isArray(res?.data) ? res.data : [];
          setSuggestions(list);
        } else {
          setSuggestions([]);
          setUserSearchError(res?.message || "Unable to find users");
        }
      })
      .catch((err) => {
        if (userSearchRequestRef.current !== requestId) return;
        setSuggestions([]);
        setUserSearchError(err?.response?.data?.message || err?.message || "Unable to find users");
      })
      .finally(() => {
        if (userSearchRequestRef.current === requestId) {
          setUserSearchLoading(false);
        }
      });
  };

  const handleSelectUser = (user) => {
    if (activeTab === "sendToUser") {
      setSelectedUsers([user]);
    } else {
      const selectedId = user?._id || user?.id;
      const exists = selectedUsers.some((u) => (u?._id || u?.id) === selectedId);
      if (!exists) setSelectedUsers((prev) => [...prev, user]);
    }
    setUserInput("");
    setSuggestions([]);
  };

  const removeUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => (u?._id || u?.id) !== userId));
  };

  const handleSendToUser = async (e) => {
    e.preventDefault();
    if (!notificationTitle.trim() || !notificationMessage.trim()) return;
    if (activeTab === "sendToUser" && selectedUsers.length === 0) return;
    if (activeTab === "bulkNotification" && selectedUsers.length === 0) return;

    if (activeTab === "sendToUser") {
      const selectedUser = selectedUsers[0] || {};
      const userId = selectedUser?._id || selectedUser?.id;
      if (!userId) {
        setSubmitMessage({ type: "error", text: "Please select a valid user." });
        return;
      }
      setSubmitLoading(true);
      setSubmitMessage({ type: "", text: "" });
      try {
        const res = await AdminService.sendNotificationToSingleUser({
          title: notificationTitle.trim(),
          message: notificationMessage.trim(),
          userId,
          link: notificationLink.trim(),
        });
        if (res?.success) {
          const created = res?.data || {};
          const newNotif = {
            _id: created?._id || "notif_" + Date.now(),
            createdAt: created?.createdAt || new Date().toISOString(),
            title: created?.title || notificationTitle.trim(),
            message: created?.message || notificationMessage.trim(),
            isActive: created?.isActive ?? true,
          };
          setNotifications((prev) => [newNotif, ...prev]);
          setSubmitMessage({ type: "success", text: res?.message || "Notification sent successfully." });
          resetInput();
          fetchNotificationList();
        } else {
          setSubmitMessage({ type: "error", text: res?.message || "Failed to send notification." });
        }
      } catch (err) {
        setSubmitMessage({
          type: "error",
          text: err?.response?.data?.message || err?.message || "Failed to send notification.",
        });
      } finally {
        setSubmitLoading(false);
      }
      return;
    }

    const userIds = selectedUsers
      .map((u) => u?._id || u?.id)
      .filter(Boolean);

    if (userIds.length === 0) {
      setSubmitMessage({ type: "error", text: "Please select at least one valid user." });
      return;
    }

    setSubmitLoading(true);
    setSubmitMessage({ type: "", text: "" });
    try {
      const res = await AdminService.sendNotificationToBulkUsers({
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        userIds,
        link: notificationLink.trim(),
      });
      if (res?.success) {
        const created = res?.data || {};
        const newNotif = {
          _id: created?._id || "notif_" + Date.now(),
          createdAt: created?.createdAt || new Date().toISOString(),
          title: created?.title || notificationTitle.trim(),
          message: created?.message || notificationMessage.trim(),
          isActive: created?.isActive ?? true,
        };
        setNotifications((prev) => [newNotif, ...prev]);
        setSubmitMessage({ type: "success", text: res?.message || "Notification sent to selected users." });
        resetInput();
        fetchNotificationList();
      } else {
        setSubmitMessage({ type: "error", text: res?.message || "Failed to send bulk notifications." });
      }
    } catch (err) {
      setSubmitMessage({
        type: "error",
        text: err?.response?.data?.message || err?.message || "Failed to send bulk notifications.",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAnnounceToAll = async (e) => {
    e.preventDefault();
    if (!notificationTitle.trim() || !notificationMessage.trim()) return;
    setSubmitLoading(true);
    setSubmitMessage({ type: "", text: "" });
    try {
      const res = await AdminService.sendNotificationToAll({
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        link: notificationLink.trim(),
      });
      if (res?.success) {
        const created = res?.data || {};
        const newNotif = {
          _id: created?._id || "notif_" + Date.now(),
          createdAt: created?.createdAt || new Date().toISOString(),
          title: created?.title || notificationTitle.trim(),
          message: created?.message || notificationMessage.trim(),
          isActive: created?.isActive ?? true,
        };
        setNotifications((prev) => [newNotif, ...prev]);
        setSubmitMessage({ type: "success", text: res?.message || "Notification sent successfully." });
        resetInput();
        fetchNotificationList();
      } else {
        setSubmitMessage({ type: "error", text: res?.message || "Failed to send notification to all users." });
      }
    } catch (err) {
      setSubmitMessage({
        type: "error",
        text: err?.response?.data?.message || err?.message || "Failed to send notification to all users.",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this notification?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    setDeletingId(id);
    setListError("");
    try {
      const res = await AdminService.deleteNotification(id);
      if (res?.success) {
        setNotifications((prev) => prev.filter((n) => n._id !== id));
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: res?.message || "Notification deleted successfully",
          timer: 1400,
          showConfirmButton: false,
        });
      } else {
        setListError(res?.message || "Failed to delete notification.");
      }
    } catch (err) {
      setListError(err?.response?.data?.message || err?.message || "Failed to delete notification.");
    } finally {
      setDeletingId("");
    }
  };

  const handleToggleActive = async (id) => {
    const row = notifications.find((n) => n._id === id);
    if (!row) return;
    const nextStatus = !row.isActive;
    setStatusUpdatingId(id);
    setListError("");
    try {
      const res = await AdminService.updateNotificationStatus(id, nextStatus);
      if (res?.success) {
        setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, isActive: nextStatus } : n)));
      } else {
        setListError(res?.message || "Failed to update notification status.");
      }
    } catch (err) {
      setListError(err?.response?.data?.message || err?.message || "Failed to update notification status.");
    } finally {
      setStatusUpdatingId("");
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage notification-management-page">
      <section className="dashboard-hero notification-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Notifications</span>
          </p>
          <h1 className="dashboard-hero__title">Notification Management</h1>
          <p className="dashboard-hero__subtitle">Send and manage platform notifications</p>
        </div>
        <div className="notification-hero__icon">
          <i className="fas fa-bell" />
        </div>
      </section>

      <div className="dashboard-body">
        {notifications.length > 0 && (
          <div className="notification-stats">
            <div className="notification-stat-card">
              <span className="notification-stat-card__value">{counts.total}</span>
              <span className="notification-stat-card__label">Total Notifications</span>
            </div>
            <div className="notification-stat-card notification-stat-card--active">
              <span className="notification-stat-card__value">{counts.active}</span>
              <span className="notification-stat-card__label">Active</span>
            </div>
            <div className="notification-stat-card notification-stat-card--inactive">
              <span className="notification-stat-card__value">{counts.inactive}</span>
              <span className="notification-stat-card__label">Inactive</span>
            </div>
          </div>
        )}

        <div className="notification-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`notification-tab ${activeTab === tab.key ? "notification-tab--active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <i className={`fas ${tab.icon}`} /> {tab.label}
            </button>
          ))}
        </div>

        <div className="notification-content">
          {(activeTab === "sendToUser" || activeTab === "bulkNotification") && (
            <section className="notification-section">
              <h2 className="notification-section__title">
                <i className={activeTab === "sendToUser" ? "fas fa-user" : "fas fa-users"} />
                {activeTab === "sendToUser" ? "Send Notification to User" : "Send Bulk Notifications"}
              </h2>
              <form className="notification-form" onSubmit={handleSendToUser}>
                <div className="notification-form__group notification-form__group--relative">
                  <label>Select User <span className="required">*</span></label>
                  <input
                    type="search"
                    placeholder="Search by email"
                    value={userInput}
                    onChange={(e) => handleFindUser(e.target.value)}
                    autoComplete="off"
                    disabled={activeTab === "sendToUser" && selectedUsers.length > 0}
                  />
                  {userInput && userSearchLoading && (
                    <ul className="notification-suggestions">
                      <li className="notification-suggestion-item">
                        <div className="notification-suggestion-email">Searching users...</div>
                      </li>
                    </ul>
                  )}
                  {userInput && suggestions.length > 0 && (
                    <ul className="notification-suggestions">
                      {suggestions.map((user) => (
                        <li key={user._id} className="notification-suggestion-item" onClick={() => handleSelectUser(user)}>
                          <div className="notification-suggestion-email">{user.emailId}</div>
                          <div className="notification-suggestion-name">{user.firstName} {user.lastName}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {userInput && !userSearchLoading && suggestions.length === 0 && !userSearchError && (
                    <ul className="notification-suggestions">
                      <li className="notification-suggestion-item">
                        <div className="notification-suggestion-email">No users found</div>
                      </li>
                    </ul>
                  )}
                  {!!userSearchError && (
                    <p className="notification-form__error">{userSearchError}</p>
                  )}
                </div>
                {selectedUsers.length > 0 && (
                  <div className="notification-selected-users">
                    {selectedUsers.map((user) => (
                      <div key={user._id || user.id} className="notification-selected-user">
                        <div className="notification-selected-user-info">
                          <span className="notification-selected-user-name">{user.emailId || "Selected user"}</span>
                          <span className="notification-selected-user-email">{user.emailId}</span>
                        </div>
                        <button type="button" className="notification-btn-remove" onClick={() => removeUser(user._id || user.id)}><i className="fas fa-times" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="notification-form__group">
                  <label>Title <span className="required">*</span></label>
                  <input type="text" placeholder="Enter notification title" value={notificationTitle} onChange={(e) => setNotificationTitle(e.target.value)} required />
                </div>
                <div className="notification-form__group">
                  <label>Message <span className="required">*</span></label>
                  <textarea placeholder="Enter notification message" value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} rows={5} required />
                </div>
                <div className="notification-form__group">
                  <label>Link (optional)</label>
                  <input type="text" placeholder="Enter link URL" value={notificationLink} onChange={(e) => setNotificationLink(e.target.value)} />
                </div>
                <div className="notification-form__actions">
                  <button type="submit" className="notification-btn-send" disabled={submitLoading}>
                    <i className="fas fa-paper-plane" /> {activeTab === "sendToUser" ? "Send Notification" : "Send Bulk Notifications"}
                  </button>
                </div>
                {!!submitMessage.text && (
                  <p className={submitMessage.type === "error" ? "notification-form__error" : "notification-form__success"}>
                    {submitMessage.text}
                  </p>
                )}
              </form>
            </section>
          )}

          {activeTab === "announceToAll" && (
            <section className="notification-section">
              <h2 className="notification-section__title"><i className="fas fa-broadcast-tower" /> Send Notifications to All Users</h2>
              <form className="notification-form" onSubmit={handleAnnounceToAll}>
                <div className="notification-form__group">
                  <label>Title <span className="required">*</span></label>
                  <input type="text" placeholder="Enter notification title" value={notificationTitle} onChange={(e) => setNotificationTitle(e.target.value)} required />
                </div>
                <div className="notification-form__group">
                  <label>Message <span className="required">*</span></label>
                  <textarea placeholder="Enter notification message" value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} rows={5} required />
                </div>
                <div className="notification-form__group">
                  <label>Link (optional)</label>
                  <input type="text" placeholder="Enter link URL" value={notificationLink} onChange={(e) => setNotificationLink(e.target.value)} />
                </div>
                <div className="notification-form__actions">
                  <button type="submit" className="notification-btn-send" disabled={submitLoading}>
                    <i className="fas fa-paper-plane" /> Send Notifications to All
                  </button>
                </div>
                {!!submitMessage.text && (
                  <p className={submitMessage.type === "error" ? "notification-form__error" : "notification-form__success"}>
                    {submitMessage.text}
                  </p>
                )}
              </form>
            </section>
          )}

          {activeTab === "managementNotification" && (
            <section className="notification-section">
              <h2 className="notification-section__title"><i className="fas fa-list-alt" /> Notification List ({notifications.length})</h2>
              {listLoading ? (
                <div className="notification-empty"><i className="fas fa-spinner fa-spin" /><p>Loading notifications...</p></div>
              ) : listError ? (
                <div className="notification-empty"><i className="fas fa-exclamation-circle" /><p>{listError}</p></div>
              ) : notifications.length === 0 ? (
                <div className="notification-empty"><i className="fas fa-bell" /><p>No notifications found</p></div>
              ) : (
                <div className="notification-table-wrap">
                  <table className="notification-table">
                    <thead>
                      <tr>
                        <th>Created At</th>
                        <th>Notification Title</th>
                        <th>Message</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notifications.map((row) => (
                        <tr key={row._id}>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>{row.title || "-----"}</td>
                          <td className="notification-table__msg">{row.message || "-----"}</td>
                          <td>
                            <span className={`notification-status-badge ${row.isActive ? "notification-status-badge--active" : "notification-status-badge--inactive"}`}>
                              {row.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="notification-actions">
                              <button type="button" className="notification-action-btn notification-action-btn--delete" onClick={() => handleDelete(row._id)} disabled={deletingId === row._id}>
                                <i className="fas fa-trash" /> Delete
                              </button>
                              {row.isActive ? (
                                <button type="button" className="notification-action-btn notification-action-btn--inactive" onClick={() => handleToggleActive(row._id)} disabled={statusUpdatingId === row._id}>
                                  <i className="fas fa-times-circle" /> Inactive
                                </button>
                              ) : (
                                <button type="button" className="notification-action-btn notification-action-btn--active" onClick={() => handleToggleActive(row._id)} disabled={statusUpdatingId === row._id}>
                                  <i className="fas fa-check-circle" /> Active
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </main>
  );
};

export default NotificationManagementPage;
